import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Skeleton,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';

import { UserContext } from '../../context/UserContext';
import PageContainer from '../../components/page-container/PageContainer';
import ProjectCard from '../../components/project-card/ProjectCard';
import { StatCardSkeleton, CardSkeleton } from '../../components/skeleton';
import ProjectApi from '../../services/ProjectApi';
import DocumentApi from '../../services/DocumentApi';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const StatCard = ({ icon, label, value, loading, color = 'primary.main' }) => {
  if (loading) {
    return <StatCardSkeleton height={120} />;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        minHeight: 120,
      }}
    >
      <Box sx={{ color, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {label}
      </Typography>
    </Paper>
  );
};

const ConsumerDashboardPage = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalDocuments: 0,
    viewedDocuments: 0,
    downloadedDocuments: 0,
  });
  const [projects, setProjects] = useState([]);
  const [projectStats, setProjectStats] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) return;

      const projectApi = new ProjectApi();
      const documentApi = new DocumentApi();

      try {
        // Get projects the consumer participates in
        const projectsRes = await projectApi.getParticipantProjects(user.uid, null, 20, 1);

        if (projectsRes.success && projectsRes.projects) {
          setProjects(projectsRes.projects);

          let totalDocs = 0;
          let viewedDocs = 0;
          let downloadedDocs = 0;
          const statsMap = {};

          // Fetch stats for each project
          for (const project of projectsRes.projects) {
            const statsRes = await documentApi.getDocumentStats(project.id);
            if (statsRes.success) {
              statsMap[project.id] = statsRes.stats;
              totalDocs += statsRes.stats.total;
              viewedDocs += statsRes.stats.viewed;
              downloadedDocs += statsRes.stats.downloaded;
            }
          }

          setProjectStats(statsMap);
          setStats({
            totalProjects: projectsRes.projects.length,
            totalDocuments: totalDocs,
            viewedDocuments: viewedDocs,
            downloadedDocuments: downloadedDocs,
          });
        }
      } catch (error) {
        console.error('Error fetching consumer dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.uid]);

  if (!user?.uid) {
    return (
      <PageContainer>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="text" width={400} height={24} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[...Array(4)].map((_, index) => (
            <Grid size={{ xs: 6, sm: 3 }} key={index}>
              <StatCardSkeleton height={120} />
            </Grid>
          ))}
        </Grid>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            {[...Array(3)].map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <CardSkeleton showStats />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Greeting */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {getGreeting()}, {user?.name || t('consumerDashboard.consumer')}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('consumerDashboard.welcome_message')}
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<FolderIcon sx={{ fontSize: 36 }} />}
            label={t('consumerDashboard.projects')}
            value={stats.totalProjects}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<DescriptionIcon sx={{ fontSize: 36 }} />}
            label={t('consumerDashboard.documents_available')}
            value={stats.totalDocuments}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<VisibilityIcon sx={{ fontSize: 36 }} />}
            label={t('consumerDashboard.viewed')}
            value={stats.viewedDocuments}
            loading={loading}
            color="warning.main"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            icon={<DownloadIcon sx={{ fontSize: 36 }} />}
            label={t('consumerDashboard.downloaded')}
            value={stats.downloadedDocuments}
            loading={loading}
            color="success.main"
          />
        </Grid>
      </Grid>

      {/* Projects Section */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('consumerDashboard.your_projects')}</Typography>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/project')}
          >
            {t('consumerDashboard.view_all')}
          </Button>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {loading ? (
          <Grid container spacing={3}>
            {[...Array(3)].map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <CardSkeleton showStats />
              </Grid>
            ))}
          </Grid>
        ) : projects.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('consumerDashboard.no_projects')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('consumerDashboard.no_projects_hint')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={project.id}>
                <ProjectCard
                  project={project}
                  stats={projectStats[project.id]}
                  onClick={() => navigate(`/project/${project.id}`)}
                  showStats
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </PageContainer>
  );
};

export default ConsumerDashboardPage;
